import axios from "axios";
import { currentRoute, reloadToLogin } from "@/lib/router-mode";

// REACT_APP_BACKEND_URL is baked in at build time. Unset, the old code produced
// the literal string "undefined/api" — every request went to
// https://host/undefined/api/... and 404'd, while the build succeeded and the
// app loaded normally. Nothing errored; only the network tab showed it.
//
// Empty string means same-origin, which is the normal deployment shape: the
// API is proxied under /api on the host serving this bundle. Set the variable
// only when the API lives somewhere else.
const RAW_BASE = process.env.REACT_APP_BACKEND_URL;

if (RAW_BASE === undefined && typeof console !== "undefined") {
  console.warn(
    "[api] REACT_APP_BACKEND_URL is not set — assuming the API is same-origin " +
    "at /api. Set it at BUILD time if the backend is on another host."
  );
}

// Trailing slashes would produce "//api".
export const API_BASE = (RAW_BASE || "").replace(/\/+$/, "");
const API = `${API_BASE}/api`;

// Electron loads this bundle from disk, so the page origin is file:// and a
// same-origin "/api" resolves to file:///api — every request fails with no
// useful error, looking exactly like the server being down.
//
// REACT_APP_BACKEND_URL is a BUILD-time variable, so this cannot be fixed by
// configuring the installed app. It has to be set when the frontend is built,
// before scripts/copy-web.js bundles it.
if (!API_BASE &&
    typeof window !== "undefined" &&
    window.location &&
    window.location.protocol === "file:") {
  const msg =
    "[api] FATAL: running from file:// with no REACT_APP_BACKEND_URL. Every " +
    "request will go to file:///api and fail. Rebuild the frontend with " +
    "REACT_APP_BACKEND_URL=https://your-server before packaging the desktop app.";
  console.error(msg);
  // Visible without opening devtools — a packaged app usually has none.
  if (typeof document !== "undefined") {
    window.addEventListener("DOMContentLoaded", () => {
      const bar = document.createElement("div");
      bar.textContent = msg;
      bar.style.cssText =
        "position:fixed;inset:0 0 auto 0;z-index:99999;background:#b00020;" +
        "color:#fff;font:12px/1.5 monospace;padding:10px;white-space:pre-wrap";
      document.body.appendChild(bar);
    });
  }
}

const instance = axios.create({ baseURL: API });

// The login request's own 401 is an answer ("Invalid credentials") that the
// Login page shows. It is not a lost session, so it must not redirect. The
// beta apply page checks a password the same way (2026-10-11), with no session.
const isLoginCall = (config) => /\/(auth\/login|beta\/apply(\/status)?)\/?$/.test(config?.url || "");

instance.interceptors.request.use((config) => {
  const token = localStorage.getItem("flyvac_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

instance.interceptors.response.use(
  (r) => r,
  (err) => {
    // Session expired or revoked: sign out and go to the login screen.
    // Route and redirect go through router-mode.js: under the desktop app's
    // HashRouter, pathname is the path to index.html and "/login" is
    // file:///login, which was a black screen (wrong password, or reopening
    // the app after the 12-hour token expired).
    if (err.response?.status === 401 && !isLoginCall(err.config) && !currentRoute().startsWith("/login")) {
      localStorage.removeItem("flyvac_token");
      localStorage.removeItem("flyvac_user");
      reloadToLogin();
    }
    return Promise.reject(err);
  }
);

export default instance;

export const auth = {
  login: (login, password) => instance.post("/auth/login", { login, password }).then((r) => r.data),
  me: () => instance.get("/auth/me").then((r) => r.data),
};

// Beta sign-up (backend/beta_apply.py). The apply calls carry the pilot's
// credentials because a closed beta gives an applicant no session.
export const betaApply = {
  // Whether sign-up is open (Admin -> Beta applications switch). Public.
  open: () => instance.get("/beta/apply/open").then((r) => r.data),
  settings: () => instance.get("/beta/settings").then((r) => r.data),
  saveSettings: (applications_open, message) =>
    instance.put("/beta/settings", { applications_open, message }).then((r) => r.data),
  status: (login, password) => instance.post("/beta/apply/status", { login, password }).then((r) => r.data),
  send: (body) => instance.post("/beta/apply", body).then((r) => r.data),
  applications: () => instance.get("/beta/applications").then((r) => r.data),
  approve: (id) => instance.post(`/beta/applications/${id}/approve`).then((r) => r.data),
  decline: (id, reason) => instance.post(`/beta/applications/${id}/decline`, { reason }).then((r) => r.data),
  testers: () => instance.get("/beta/testers").then((r) => r.data),
  addTester: (pid, note) => instance.post("/beta/testers", { pid, note }).then((r) => r.data),
  removeTester: (pid) => instance.delete(`/beta/testers/${encodeURIComponent(pid)}`).then((r) => r.data),
};

export const pilot = {
  dashboard: () => instance.get("/pilot/dashboard").then((r) => r.data),
  bids: () => instance.get("/pilot/bids").then((r) => r.data),
  cancelBid: (id) => instance.delete(`/pilot/bids/${id}`).then((r) => r.data),
  logbook: (limit = 100) => instance.get("/pilot/logbook", { params: { limit } }).then((r) => r.data),
  landingStats: () => instance.get("/pilot/landing-stats").then((r) => r.data),
  duty:         (days = 30) => instance.get("/pilot/duty", { params: { days } }).then((r) => r.data),
};

export const gates = {
  // orig/dest let the server reject the same flight number's other legs.
  lookup: (flight, orig, dest, date) =>
    instance.get("/gates/lookup", { params: { flight, orig, dest, date } }).then((r) => r.data),
};

export const dispatch = {
  threads: () => instance.get("/dispatch/threads").then((r) => r.data),
  thread: (id) => instance.get(`/dispatch/threads/${id}`).then((r) => r.data),
  create: (body) => instance.post("/dispatch/threads", body).then((r) => r.data),
  reply: (id, message) => instance.post(`/dispatch/threads/${id}/reply`, { body: message }).then((r) => r.data),
  setStatus: (id, status) => instance.put(`/dispatch/threads/${id}/status`, null, { params: { status } }).then((r) => r.data),
  remove: (id) => instance.delete(`/dispatch/threads/${id}`).then((r) => r.data),
  unread: () => instance.get("/dispatch/unread-count").then((r) => r.data),
  getAvailability: () => instance.get("/dispatch/admin/availability").then((r) => r.data),
  setAvailability: (available) => instance.put("/dispatch/admin/availability", { available }).then((r) => r.data),
  onDuty: () => instance.get("/dispatch/admin/on-duty").then((r) => r.data),
};

export const aircraft = {
  types: () => instance.get("/aircraft/types").then((r) => r.data),
  registrations: (icao) => instance.get("/aircraft/registrations", { params: { icao } }).then((r) => r.data),
};

export const weather = {
  metar: (icao, taf = true) => instance.get("/weather/metar", { params: { icao, taf } }).then((r) => r.data),
};

export const ofpApi = {
  fetch:    (ofp_id) => instance.get(`/ofp/${ofp_id}`).then((r) => r.data),
  latest:   () => instance.get("/ofp/latest").then((r) => r.data),
  forBid:   (bid_id) => instance.get(`/ofp/for-bid/${bid_id}`).then((r) => r.data),
  pinToBid: (bid_id, ofp_id) => instance.put(`/ofp/for-bid/${bid_id}`, null, { params: { ofp_id } }).then((r) => r.data),
};

export const bidGates = {
  get: (bid_id) => instance.get(`/pilot/bids/${bid_id}/gates`).then((r) => r.data),
  set: (bid_id, gate_dep, gate_arr) => instance.put(`/pilot/bids/${bid_id}/gates`, { gate_dep, gate_arr }).then((r) => r.data),
};

export const prefs = {
  get:    () => instance.get("/pilot/prefs").then((r) => r.data),
  update: (body) => instance.put("/pilot/prefs", body).then((r) => r.data),
};

export const schedule = {
  list: (params = {}) => instance.get("/schedule", { params }).then((r) => r.data),
};

export const acars = {
  active: () => instance.get("/acars/active").then((r) => r.data),
  // The route flown so far, from the server's track (vacanada_tracks).
  track: (id) => instance.get(`/acars/${id}/track`).then((r) => r.data),
  init: (body) => instance.post("/acars/init", body).then((r) => r.data),
  telemetry: (id, body) => instance.post(`/acars/${id}/telemetry`, body).then((r) => r.data),
  off: (id) => instance.post(`/acars/${id}/off`).then((r) => r.data),
  on: (id, body) => instance.post(`/acars/${id}/on`, body).then((r) => r.data),
  pirep: (id, body) => instance.post(`/acars/${id}/pirep`, body).then((r) => r.data),
  cancel: (id) => instance.post(`/acars/${id}/cancel`).then((r) => r.data),
};

export const live = {
  flights: () => instance.get("/live/flights").then((r) => r.data),
  vatsim:  () => instance.get("/live/vatsim").then((r) => r.data),
  atc:     (icaos) => instance.get("/live/vatsim/atc",
             { params: { icao: [].concat(icaos).filter(Boolean).join(",") } }).then((r) => r.data),
};

export const faa = {
  health: () => instance.get("/faa/health").then((r) => r.data),
  advisories: (orig, dest) =>
    instance.get("/faa/advisories", { params: { orig, dest } }).then((r) => r.data),
  forActive: () => instance.get("/faa/for-active").then((r) => r.data),
};
